import { onboardingService } from '../../../services/onboarding.ts';
import { OrganizationService } from '../../../services/organization.ts';
import { authService } from '../../../services/auth.ts';
import {
  MODULE_SECRETS,
  MODULE_AUDIT_LOGS,
  GATEWAY_MODULES
} from '../../../models/modules.ts';

import { setToken } from '../../../utils/session.ts';

declare var Stripe: any;

interface PlanLimits {
  data_allowance_gb: number;
  overage_rate_cents: number;
  config_history_limit: number;
  mau_allowance: number;
  mau_overage_rate_cents: number;
  is_byoc?: boolean;
}

export class AppOnboarding extends HTMLElement {
  private step = 1; // 1: Org, 2: Config & Checkout
  private orgName = '';
  private isSubmitting = false;
  private plans: any[] = [];
  private availableModules: any[] = [];
  private isLoadingData = false;

  private selectedPlanId: string = '';
  private selectedModuleIds: Set<string> = new Set();

  // Billing State
  private stripe: any = null;
  private cardElement: any = null;
  private clientSecret: string | null = null;
  private stripePk: string | null = null;
  private createdOrgId: string | null = null;
  private billingAmount: number = 0;
  private discountAmount: number = 0;
  private voucherCode: string = '';
  private isVoucherApplying = false;
  private voucherMessage: string = '';
  private voucherIsError = false;
  private isUpdatingBilling = false;
  private hasOtherOrgs = false;

  constructor() {
    super();
  }

  async connectedCallback() {
    this.setupListeners();

    // 1. Check if we are returning from a Stripe redirect
    const url = new URL(window.location.href);
    const pi = url.searchParams.get('payment_intent');
    console.debug('>>> AppOnboarding: Connected. URL params:', { pi });

    // 2. Check if user already has an organization (partially onboarded)
    const profile = await authService.getProfile();
    const savedOrgId = localStorage.getItem('crate_onboarding_org_id');

    // Only trust savedOrgId if it matches an organization the user actually belongs to.
    // This prevents skipping to Step 2 with a stale ID after a DB wipe or logout/login.
    const userOrgs = profile?.organizations || [];
    const isSavedOrgValid = savedOrgId && userOrgs.some((o: any) => o.id === savedOrgId);

    let orgId = profile?.org_id || (isSavedOrgValid ? savedOrgId : null);
    
    if (this.getAttribute('mode') === 'new-org') {
      orgId = null; // Force start at step 1
    }
    
    // Check if the user has other organizations to fall back to if they cancel
    this.hasOtherOrgs = userOrgs.length > 1 || (userOrgs.length === 1 && userOrgs[0].id !== orgId);

    console.debug('>>> AppOnboarding: Profile/Storage check:', {
      profileOrgId: profile?.org_id,
      savedOrgId,
      isSavedOrgValid,
      finalOrgId: orgId,
      hasOtherOrgs: this.hasOtherOrgs
    });

    // Recover configuration state from localStorage if available
    const savedPlan = localStorage.getItem('crate_onboarding_plan');
    if (savedPlan !== null) this.selectedPlanId = savedPlan;
    const savedModules = localStorage.getItem('crate_onboarding_modules');
    if (savedModules !== null) {
      try { this.selectedModuleIds = new Set(JSON.parse(savedModules as string) as string[]); } catch (e) { }
    }

    if (pi) {
      await this.handleStripeReturn(pi);
      return;
    }

    if (orgId) {
      // If they already have a plan, they shouldn't be here, but let's be safe
      if (profile?.plan_id) {
        console.debug('>>> AppOnboarding: Already fully onboarded, triggering transition');
        window.dispatchEvent(new CustomEvent('auth-state-changed', {
          detail: { isAuthenticated: true }
        }));
        return;
      }

      console.debug('>>> AppOnboarding: Resuming at step 2 with org:', orgId);
      this.createdOrgId = orgId;
      this.step = 2;
      await this.loadData(orgId);
    } else {
      console.debug('>>> AppOnboarding: Starting at step 1');
      this.render();
    }
  }

  private async handleStripeReturn(paymentIntentId: string) {
    // 1. Immediately clean up URL to prevent double-processing on reloads
    window.history.replaceState({}, document.title, window.location.pathname);

    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.render(); // Show loading/confirming state

    try {
      const profile = await authService.getProfile();
      const savedOrgId = localStorage.getItem('crate_onboarding_org_id');
      const orgId = profile?.org_id || savedOrgId || this.createdOrgId;

      console.debug('>>> AppOnboarding: Handling Stripe return for org:', orgId);
      if (!orgId) throw new Error("No organization context found for payment confirmation");

      // Recover state for the backend call
      const savedPlan = localStorage.getItem('crate_onboarding_plan');
      const savedModules = localStorage.getItem('crate_onboarding_modules');
      const moduleIds = savedModules !== null ? JSON.parse(savedModules as string) as string[] : Array.from(this.selectedModuleIds);
      const planId = savedPlan || this.selectedPlanId;

      // Confirm with backend
      const response = await OrganizationService.getInstance().confirmPayment(
        orgId as string,
        paymentIntentId,
        planId,
        moduleIds
      );

      // Success! Clean up localStorage
      localStorage.removeItem('crate_onboarding_plan');
      localStorage.removeItem('crate_onboarding_modules');
      localStorage.removeItem('crate_onboarding_org_id'); // Also clear the org ID now

      const newToken = response.token || (response.data && response.data.token);
      if (newToken) {
        setToken(newToken);
      } else {
        await authService.getProfile();
        window.dispatchEvent(new CustomEvent('auth-state-changed', {
          detail: { isAuthenticated: true }
        }));
      }
    } catch (e) {
      console.error("Failed to handle Stripe return", e);
      this.step = 1; // Fallback
      this.render();
    } finally {
      this.isSubmitting = false;
    }
  }

  private async loadData(orgId: string) {
    this.isLoadingData = true;
    this.render();
    try {
      const [plans, modules] = await Promise.all([
        OrganizationService.getInstance().getPlans(),
        OrganizationService.getInstance().getAvailableModules(orgId)
      ]);
      this.plans = plans;
      this.availableModules = modules;

      // Default to Monthly plan if available, else first plan
      const monthly = plans.find(p => p.interval === 'month');
      if (monthly) {
        this.selectedPlanId = monthly.id;
      } else if (plans.length > 0) {
        this.selectedPlanId = plans[0].id;
      }

      await this.updateBillingSetup();

    } catch (e) {
      console.error('Failed to load onboarding data', e);
      // If we failed to load data for a saved org ID, it's likely stale/invalid
      if (orgId === localStorage.getItem('crate_onboarding_org_id')) {
          localStorage.removeItem('crate_onboarding_org_id');
          localStorage.removeItem('crate_onboarding_plan');
          localStorage.removeItem('crate_onboarding_modules');
          this.step = 1;
          this.createdOrgId = null;
      }
    } finally {
      this.isLoadingData = false;
      this.render();
      if (this.step === 2) {
        this.mountStripe();
      }
    }
  }

  private async updateBillingSetup() {
    if (!this.createdOrgId) return;
    this.isUpdatingBilling = true;
    this.render();

    try {
      const user = authService.getUser();
      const res = await OrganizationService.getInstance().setupBilling(
        this.createdOrgId,
        user?.email || '',
        this.selectedPlanId,
        Array.from(this.selectedModuleIds)
      );

      this.billingAmount = res.final_amount_cents !== undefined ? res.final_amount_cents : res.amount;
      this.discountAmount = res.discount_cents || 0;
      this.clientSecret = res.client_secret;
      this.stripePk = res.stripe_pk;
      this.voucherCode = res.voucher_code || this.voucherCode;

    } catch (e) {
      console.error('Failed to update billing setup', e);
    } finally {
      this.isUpdatingBilling = false;
      // Only render if we need to update the summary amounts
      this.render();
    }
  }

  private async handleVoucherApply() {
    if (!this.createdOrgId || !this.voucherCode || this.isVoucherApplying) return;

    this.isVoucherApplying = true;
    this.voucherMessage = '';
    this.voucherIsError = false;
    this.render();

    try {
      await OrganizationService.getInstance().redeemVoucher(this.createdOrgId, this.voucherCode);
      this.voucherMessage = 'Voucher applied successfully!';
      this.voucherIsError = false;
      await this.updateBillingSetup();
    } catch (e: any) {
      this.voucherMessage = e.message || 'Invalid voucher code';
      this.voucherIsError = true;
    } finally {
      this.isVoucherApplying = false;
      this.render();
    }
  }

  private setupListeners() {
    this.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (this.step === 1) {
        await this.handleOrgSubmit();
      } else if (this.step === 2) {
        await this.handlePaymentSubmit();
      }
    });

    this.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;

      if (target.id === 'btn-cancel-onboarding') {
        if (this.createdOrgId) {
            const confirmed = window.confirm("Are you sure you want to cancel? The organization you just created will be permanently deleted and cannot be recovered.");
            if (!confirmed) return;
        }
        await this.handleCancelOnboarding();
      }
      if (target.id === 'btn-logout') {
        authService.logout();
      }

      // Plan Selection
      const planCard = target.closest('.plan-option');
      if (planCard) {
        const newId = planCard.getAttribute('data-plan-id')!;
        if (newId !== this.selectedPlanId) {
          this.selectedPlanId = newId;
          localStorage.setItem('crate_onboarding_plan', newId);
          await this.updateBillingSetup();
        }
      }

      // Module Selection
      const moduleCard = target.closest('.module-option');
      if (moduleCard && !moduleCard.classList.contains('disabled')) {
        const id = moduleCard.getAttribute('data-id')!;
        if (this.selectedModuleIds.has(id)) {
          this.selectedModuleIds.delete(id);
        } else {
          this.selectedModuleIds.add(id);
        }
        localStorage.setItem('crate_onboarding_modules', JSON.stringify(Array.from(this.selectedModuleIds)));
        await this.updateBillingSetup();
      }

      // Voucher Application
      if (target.id === 'btn-apply-voucher') {
        await this.handleVoucherApply();
      }
    });

    this.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.id === 'voucher-input') {
        this.voucherCode = target.value;
      }
    });
  }

  private async handleCancelOnboarding() {
    try {
      const profile = await authService.getProfile();
      const currentOrgId = profile?.org_id || this.createdOrgId;
      const otherOrgs = profile?.organizations?.filter((o: any) => o.id !== currentOrgId) || [];
      
      let targetOrgId = otherOrgs.length > 0 ? otherOrgs[0].id : null;
      if (!targetOrgId) {
          targetOrgId = localStorage.getItem('crate_fallback_org_id');
      }
      
      // If we just created an organization during this flow, delete it to prevent orphan orgs
      if (this.createdOrgId) {
          try {
              await OrganizationService.getInstance().delete(this.createdOrgId);
          } catch (e) {
              console.error("Failed to delete cancelled organization", e);
          }
      }

      if (targetOrgId) {
        // Switch to the first available other organization
        await authService.switchOrganization(targetOrgId);
        
        // Clean up onboarding state
        localStorage.removeItem('crate_onboarding_plan');
        localStorage.removeItem('crate_onboarding_modules');
        localStorage.removeItem('crate_onboarding_org_id');
        localStorage.removeItem('crate_fallback_org_id');
        
        // AppRoot will re-render automatically on auth-state-changed event
      } else {
        authService.logout();
      }
    } catch (e) {
      console.error('Failed to cancel onboarding', e);
    }
  }

  private resolvePlanLimits(moduleIds: string[]): PlanLimits {
    const limits: PlanLimits = {
      data_allowance_gb: 0,
      overage_rate_cents: 0,
      config_history_limit: 0,
      mau_allowance: 0,
      mau_overage_rate_cents: 0,
      is_byoc: false
    };

    for (const id of moduleIds) {
      const m = this.availableModules.find(am => am.id === id);
      if (!m) continue;

      if (m.data_allowance_gb > limits.data_allowance_gb) limits.data_allowance_gb = m.data_allowance_gb;

      if (m.overage_rate_cents > 0) {
        if (limits.overage_rate_cents === 0 || m.overage_rate_cents < limits.overage_rate_cents) {
          limits.overage_rate_cents = m.overage_rate_cents;
        }
      }

      if (m.mau_allowance > limits.mau_allowance) limits.mau_allowance = m.mau_allowance;
      
      if (m.mau_overage_rate_cents > 0) {
        if (limits.mau_overage_rate_cents === 0 || m.mau_overage_rate_cents < limits.mau_overage_rate_cents) {
          limits.mau_overage_rate_cents = m.mau_overage_rate_cents;
        }
      }

      if (m.config_history_limit === 0) {
        limits.config_history_limit = 0;
      } else if (limits.config_history_limit !== 0 && m.config_history_limit > limits.config_history_limit) {
        limits.config_history_limit = m.config_history_limit;
      }

      if (id.includes('byoc')) {
        limits.is_byoc = true;
        limits.data_allowance_gb = 0;
        limits.overage_rate_cents = 0;
        limits.config_history_limit = 0;
      }
    }

    return limits;
  }

  private async handleOrgSubmit() {
    if (!this.orgName || this.isSubmitting) return;
    this.isSubmitting = true;
    this.render();
    try {
      const org = await onboardingService.createOrganization(this.orgName);
      this.createdOrgId = org.id!;
      localStorage.setItem('crate_onboarding_org_id', this.createdOrgId);
      await this.loadData(this.createdOrgId);
      this.step = 2;
    } catch (e) {
      console.error('Failed to create organization', e);
    } finally {
      this.isSubmitting = false;
      this.render();
    }
  }

  private mountStripe() {
    if (!this.stripePk || !this.clientSecret) return;

    // Initialize Stripe only once
    if (!this.stripe) {
      this.stripe = Stripe(this.stripePk);
    }

    // Create card element only once
    if (!this.cardElement) {
      const elements = this.stripe.elements();
      this.cardElement = elements.create('card', {
        style: {
          base: {
            color: '#ffffff',
            fontFamily: 'inherit',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': { color: '#94a3b8' }
          },
          invalid: { color: '#ef4444', iconColor: '#ef4444' }
        }
      });
    }

    const mountPoint = this.querySelector('#card-element');
    if (mountPoint && !mountPoint.hasChildNodes()) {
      this.cardElement.mount('#card-element');
    }
  }

  private async handlePaymentSubmit() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    // Surgical UI update to avoid wiping Stripe
    const btn = this.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = btn ? btn.innerHTML : 'Complete Subscription';
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<div class="spinner"></div> Confirming...';
    }

    try {
      const result = await this.stripe.confirmCardPayment(this.clientSecret, {
        payment_method: { card: this.cardElement }
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      // Now confirm with our backend
      const response = await OrganizationService.getInstance().confirmPayment(
        this.createdOrgId!,
        result.paymentIntent.id,
        this.selectedPlanId,
        Array.from(this.selectedModuleIds)
      );

      // Success! Clean up localStorage
      localStorage.removeItem('crate_onboarding_plan');
      localStorage.removeItem('crate_onboarding_modules');
      localStorage.removeItem('crate_onboarding_org_id');

      // Update token if returned (backend should return a token with updated orgId/planId claims)
      const newToken = response.token || (response.data && response.data.token);
      console.debug('>>> AppOnboarding: confirmPayment success. New token:', !!newToken);
      if (newToken) {
        setToken(newToken);
      } else {
        // Success! Refresh profile and app shell will take over
        await authService.getProfile();
        window.dispatchEvent(new CustomEvent('auth-state-changed', {
          detail: { isAuthenticated: true }
        }));
      }

    } catch (e: any) {
      console.error('Payment failed', e);
      const errEl = this.querySelector('#card-errors');
      if (errEl) errEl.textContent = e.message;

      // Reset button if failed
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = originalText;
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private render() {
    const scrollContainer = this.querySelector('.onboarding-wrapper');
    const scrollPos = scrollContainer ? scrollContainer.scrollTop : 0;

    this.innerHTML = `
      <style>
        .onboarding-wrapper {
          display: flex;
          align-items: ${this.step === 1 ? 'center' : 'flex-start'};
          justify-content: center;
          width: 100%;
          height: 100vh;
          background-color: var(--bg-main);
          padding: 40px 20px;
          box-sizing: border-box;
          overflow-y: auto;
          overflow-x: hidden;
          scroll-behavior: auto;
        }

        /* Custom Scrollbar to match app-shell */
        .onboarding-wrapper::-webkit-scrollbar { width: 12px; height: 12px; }
        .onboarding-wrapper::-webkit-scrollbar-track { background: rgba(0,0,0,0.2); border-left: 1px solid var(--border-sketch); }
        .onboarding-wrapper::-webkit-scrollbar-thumb { background: rgba(245, 158, 11, 0.2); border-radius: 20px; border: 3px solid transparent; background-clip: content-box; }
        .onboarding-wrapper::-webkit-scrollbar-thumb:hover { background: rgba(245, 158, 11, 0.5); background-clip: content-box; }
        .onboarding-card {
          width: 100%;
          max-width: 1000px;
          padding: 40px 48px;
          border-radius: 12px;
          margin-bottom: 60px;
          position: relative;
        }
        h1 { margin: 0 0 8px 0; font-size: 28px; font-weight: 800; letter-spacing: -0.02em; }
        .subtitle { color: var(--text-dim); margin-bottom: 20px; font-size: 15px; }
        
        .input-group { margin-bottom: 24px; }
        label { display: block; margin-bottom: 8px; font-size: 13px; font-weight: 600; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.05em; }
        input[type="text"] {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          color: white;
          font-size: 16px;
          outline: none;
          transition: border-color 0.2s;
        }
        input:focus { border-color: var(--accent); }

        .config-grid {
            display: grid;
            grid-template-columns: 1fr 320px;
            gap: 40px;
        }
        
        .plans-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 24px; }
        .plan-option {
            padding: 20px; cursor: pointer; transition: all 0.2s;
            background: rgba(255,255,255,0.03); border-radius: 8px;
        }
        .plan-option:hover { background: rgba(255,255,255,0.06); }
        .plan-option.selected::before { border-color: var(--accent); }
        .plan-option.selected { background: rgba(245, 158, 11, 0.05); }

        .plan-features {
            list-style: none;
            padding: 0;
            margin: 16px 0 0 0;
            font-size: 11px;
            color: var(--text-dim);
            border-top: 1px solid rgba(255,255,255,0.05);
            padding-top: 12px;
        }
        .plan-features li {
            margin-bottom: 4px;
            display: flex;
            align-items: center;
            gap: 6px;
        }
        .plan-features li::before { content: "•"; color: var(--accent); }

        .modules-list { display: flex; flex-direction: column; gap: 12px; }
        .module-option {
            display: flex; align-items: center; gap: 12px; padding: 12px 16px;
            background: rgba(255,255,255,0.03);
            border-radius: 8px; cursor: pointer; transition: all 0.2s;
        }
        .module-option:hover { background: rgba(255,255,255,0.06); }
        .module-option.selected::before { border-color: var(--accent); }
        .module-option.selected { background: rgba(245, 158, 11, 0.05); }

        .sketch-border-white::before {
            border-color: #ffffff !important;
            border-width: 2px !important;
            opacity: 0.8 !important;
        }
        
        .plans-grid, .modules-list {
            padding: 20px;
            margin-bottom: 32px;
            isolation: isolate;
        }

        .summary-box {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid var(--border-sketch);
            padding: 24px;
            position: relative;
            z-index: 5;
            display: flex; 
            flex-direction: column; 
            gap: 16px;
        }
        .summary-header {
            margin: -24px -24px 20px -24px;
            padding: 16px 24px;
            border-bottom: 1px solid var(--border-sketch);
            background: rgba(255, 255, 255, 0.02);
            color: var(--accent);
            font-size: 13px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.15em;
        }
        .summary-row { display: flex; justify-content: space-between; font-size: 14px; }
        .total-row { border-top: 1px solid rgba(255,255,255,0.1); padding-top: 16px; font-size: 20px; font-weight: 800; color: white; }
        .discount-row { color: #10b981; font-weight: 600; font-size: 13px; }

        .voucher-group { display: flex; gap: 8px; margin-top: 8px; }
        .voucher-input { flex: 1; padding: 8px 12px !important; font-size: 13px !important; background: rgba(255,255,255,0.03) !important; }
        .btn-voucher { padding: 8px 16px; background: rgba(255,255,255,0.1); border: none; color: white; border-radius: 4px; cursor: pointer; font-size: 12px; }
        .voucher-msg { font-size: 11px; margin-top: 4px; }
        .voucher-msg.error { color: #ef4444; }
        .voucher-msg.success { color: #10b981; }

        .loading-overlay {
            position: absolute; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center;
            z-index: 10;
        }

        /* Stripe */
        .checkout-section { margin-top: 16px; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 24px; }
        #card-element {
            background: rgba(255,255,255,0.05); padding: 12px; border-radius: 6px;
            border: 1px solid rgba(255,255,255,0.1); margin-bottom: 16px;
        }
        #card-errors { color: #ef4444; font-size: 12px; margin-bottom: 16px; }

        .spinner {
            width: 24px; height: 24px; border: 3px solid rgba(245, 158, 11, 0.2);
            border-top-color: var(--accent); border-radius: 50%;
            animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .btn-primary {
            width: 100%;
            padding: 14px;
            background: var(--accent);
            color: black;
            border: none;
            border-radius: 6px;
            font-weight: 800;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
        }
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.1); box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3); }
        .btn-primary:active { transform: translateY(0); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        
        .btn-secondary {
            width: 100%;
            padding: 14px;
            background: transparent;
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            font-weight: 800;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
        }
        .btn-secondary:hover { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.4); }
        .btn-secondary:active { transform: translateY(0); }
        .btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
      </style>

      <div class="onboarding-wrapper drafting-grid">
        <div class="onboarding-card sketch-border">
          ${this.renderCurrentStep()}
        </div>
      </div>
    `;

    // Restore scroll position
    const newScrollContainer = this.querySelector('.onboarding-wrapper') as HTMLElement;
    if (newScrollContainer) {
      newScrollContainer.scrollTop = scrollPos;
    }

    // Handle step 1 inputs
    if (this.step === 1) {
      const input = this.querySelector('input');
      if (input) {
        input.value = this.orgName;
        input.oninput = (e: any) => { this.orgName = e.target.value; };
      }
    }

    // Handle step 2 Stripe
    if (this.step === 2) {
      this.mountStripe();
    }
  }

  private renderCurrentStep() {
    switch (this.step) {
      case 1: return this.renderOrgStep();
      case 2: return this.renderPlanStep();
      default: return '';
    }
  }

  private renderOrgStep() {
    return `
      <h1>Create Your Organization</h1>
      <p class="subtitle">Welcome to Crate. Let's start by naming your workspace.</p>
      
      <form>
        <div class="input-group">
          <label>Organization Name</label>
          <input type="text" placeholder="e.g. Stark Industries" required autofocus />
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 32px;">
          <button type="button" id="btn-cancel-onboarding" class="btn-secondary" style="width: auto; padding: 10px 32px; min-width: 140px;" ${this.isSubmitting ? 'disabled' : ''}>
              Cancel
          </button>
          <button type="submit" class="btn-primary" style="width: auto; padding: 10px 32px; min-width: 140px;" ${this.isSubmitting ? 'disabled' : ''}>
            ${this.isSubmitting ? 'Creating...' : 'Continue'}
          </button>
        </div>
      </form>
    `;
  }

  private renderPlanStep() {
    if (this.isLoadingData) {
      return `<div style="text-align:center; padding: 60px;">Loading configuration options...</div>`;
    }

    const selectedPlan = this.plans.find(p => p.id === this.selectedPlanId);
    const isAnnual = selectedPlan?.interval === 'year';

    const addons = Array.from(this.selectedModuleIds).map(id => {
      const m = this.availableModules.find(am => am.id === id);
      return m ? { name: m.name, id: m.id, price_cents: m.price_cents } : null;
    }).filter(Boolean);

    return `
      <h1>Configure Your Plan</h1>
      <p class="subtitle">Customize your environment and complete your subscription in one step.</p>
      
      <form>
      <div class="config-grid">
        <div class="options">
          <label>Select Base Plan</label>
          <div class="plans-grid sketch-border sketch-border-white">
            ${this.plans.map(p => {
      const limits = this.resolvePlanLimits(p.modules || []);
      return `
              <div class="plan-option sketch-border ${this.selectedPlanId === p.id ? 'selected' : ''}" data-plan-id="${p.id}">
                <div style="font-weight: 800; font-size: 16px;">${p.name}</div>
                <div style="font-size: 20px; color: white; margin: 8px 0;">$${(p.price_cents / 100).toFixed(0)}<span style="font-size: 12px; opacity: 0.6;">/${p.interval}</span></div>
                <ul class="plan-features">
                    <li>${limits.data_allowance_gb === 0 ? 'Unlimited' : limits.data_allowance_gb + 'GB'} Data</li>
                    <li>${limits.overage_rate_cents === 0 ? 'No Overage' : '$' + (limits.overage_rate_cents / 100).toFixed(2) + '/GB'}</li>
                    ${limits.config_history_limit === 0 ? '<li>Unlimited History</li>' : `<li>${limits.config_history_limit} Versions</li>`}
                    ${(limits.mau_allowance > 0 || limits.mau_overage_rate_cents > 0) ? `
                      <li>${limits.mau_allowance === 0 ? 'Unlimited MAU' : limits.mau_allowance.toLocaleString() + ' MAU'}</li>
                      <li>${limits.mau_overage_rate_cents === 0 ? 'No MAU Overage' : '$' + (limits.mau_overage_rate_cents / 100).toFixed(2) + '/MAU'}</li>
                    ` : ''}
                </ul>
              </div>
            `}).join('')}
          </div>

          <label>Additional Modules</label>
          <div class="modules-list sketch-border sketch-border-white">
            ${this.availableModules.map(m => {
        const isIncluded = selectedPlan?.modules?.includes(m.id);
        const isSelected = this.selectedModuleIds.has(m.id) || isIncluded;

        return `
              <div class="module-option sketch-border ${isSelected ? 'selected' : ''} ${isIncluded ? 'disabled' : ''}" 
                   data-id="${m.id}" 
                   style="${isIncluded ? 'opacity: 0.6; cursor: default;' : 'cursor: pointer;'}">
                <div style="flex: 1;">
                    <div style="font-weight: 700;">${m.name} ${isIncluded ? '<span style="font-size: 10px; color: var(--accent);">(Included)</span>' : ''}</div>
                    <div style="font-size: 12px; opacity: 0.6;">${m.description}</div>
                </div>
                <div style="text-align: right;">
                    <div style="font-weight: 800;">${isIncluded ? '—' : '$' + ((isAnnual ? m.price_cents * 12 : m.price_cents) / 100).toFixed(0)}</div>
                    <div style="font-size: 10px; opacity: 0.6;">${isIncluded ? '' : '/' + (isAnnual ? 'yr' : 'mo')}</div>
                </div>
              </div>
            `}).join('')}
          </div>
        </div>

          <div class="summary-box sketch-border">
            ${this.isUpdatingBilling ? `<div class="loading-overlay"><div class="spinner"></div></div>` : ''}
            
            <div class="summary-header">Order Summary</div>
            
            <div class="summary-details" style="display: flex; flex-direction: column; gap: 8px;">
                <div class="summary-row">
                    <span>${selectedPlan?.name}</span>
                    <span>$${((selectedPlan?.price_cents || 0) / 100).toFixed(2)}</span>
                </div>
                
                ${addons.map(a => `
                    <div class="summary-row" style="opacity: 0.7;">
                        <span>+ ${a!.name}</span>
                        <span style="font-size: 12px;">$${((isAnnual ? a!.price_cents * 12 : a!.price_cents) / 100).toFixed(2)}</span>
                    </div>
                `).join('')}

                <div class="summary-row" style="margin-top: 8px; border-top: 1px dashed rgba(255,255,255,0.1); padding-top: 8px; font-weight: 600;">
                    <span>Subtotal</span>
                    <span>$${(((selectedPlan?.price_cents || 0) + addons.reduce((sum, a) => sum + (isAnnual ? a!.price_cents * 12 : a!.price_cents), 0)) / 100).toFixed(2)}</span>
                </div>

                ${this.discountAmount > 0 ? `
                    <div class="summary-row discount-row" style="color: var(--accent);">
                        <span>Voucher Discount</span>
                        <span>-$${(this.discountAmount / 100).toFixed(2)}</span>
                    </div>
                ` : ''}
            </div>

            <div class="voucher-section">
                <label style="font-size: 11px; margin-bottom: 4px;">Voucher / Promo Code</label>
                <div class="voucher-group">
                    <input type="text" id="voucher-input" class="voucher-input" placeholder="Enter code" value="${this.voucherCode}" />
                    <button type="button" id="btn-apply-voucher" class="btn-voucher" ${this.isVoucherApplying ? 'disabled' : ''}>
                        ${this.isVoucherApplying ? '...' : 'Apply'}
                    </button>
                </div>
                ${this.voucherMessage ? `<div class="voucher-msg ${this.voucherIsError ? 'error' : 'success'}">${this.voucherMessage}</div>` : ''}
            </div>

            <div class="total-row summary-row" style="border-top: 2px solid white; margin-top: 12px; padding-top: 12px; font-size: 18px; font-weight: 800;">
                <span>Total Due Now</span>
                <span>$${(this.billingAmount / 100).toFixed(2)}</span>
            </div>

            <div class="checkout-section">
                <label style="margin-bottom: 12px; display: block;">Secure Payment</label>
                <div id="card-element"></div>
                <div id="card-errors"></div>
                
                <div style="display: flex; gap: 12px; margin-top: 16px;">
                  <button type="submit" class="btn-primary" ${this.isSubmitting || this.isUpdatingBilling ? 'disabled' : ''} style="flex: 2;">
                      ${this.isSubmitting ? 'Confirming...' : 'Complete Subscription'}
                  </button>
                  <button type="button" id="btn-cancel-onboarding" class="btn-secondary" ${this.isSubmitting ? 'disabled' : ''} style="flex: 1;">
                      Cancel Setup
                  </button>
                </div>
                <p style="font-size: 10px; opacity: 0.5; margin-top: 12px; text-align: center;">
                    By clicking "Complete Subscription", you agree to our terms of service and authorize the prorated charge of $${(this.billingAmount / 100).toFixed(2)} to your card.
                </p>
            </div>
        </div>
      </div>
      </form>
    `;
  }


}

if (!customElements.get('app-onboarding')) {
  customElements.define('app-onboarding', AppOnboarding);
}
